import { AttachmentStatus, UploadableFileAttachment } from '@/utils/fetchers/attachment'
import { MIME_TYPES } from '@mantine/dropzone'
import { compact, isEmpty, uniqueId } from 'lodash-es'
import {
	type Attachment,
	AttachmentType,
	type FileAttachment,
	type LinkAttachment,
} from '../../utils/fetchers/attachment'
import { FormAttachment } from './schema'

export function filterPicAttachments(attachments: Attachment[]): FileAttachment[] {
	return attachments.filter((item) => item.type === AttachmentType.PICTURE) as FileAttachment[]
}

export function filterVideoAttachments(attachments: Attachment[]): FileAttachment[] {
	return attachments.filter((item) => item.type === AttachmentType.VIDEO) as FileAttachment[]
}

export function filterLinkAttachments(attachments: Attachment[]): LinkAttachment[] {
	return attachments.filter((item) => item.type === AttachmentType.LINK)
}

export function filterFileAttachments(attachments: Attachment[]): FileAttachment[] {
	return attachments.filter((item) => item.type !== AttachmentType.LINK)
}

export function filterWaitToUploadAttachments(
	attachments: FileAttachment[]
): UploadableFileAttachment[] {
	return attachments.filter(
		(item) => item.status === AttachmentStatus.WAIT_TO_UPLOAD && !!item.file
	) as UploadableFileAttachment[]
}

export function filterUploadingAttachments(attachments: FileAttachment[]): FileAttachment[] {
	return attachments.filter((item) => item.status === AttachmentStatus.UPLOADING)
}

export function checkAttachmentPickerDisableStatus(
	attachments: Attachment[]
): Record<AttachmentType, boolean> {
	const pictures = filterPicAttachments(attachments)
	const videos = filterVideoAttachments(attachments)
	const links = filterLinkAttachments(attachments)
	const hasVideo = !isEmpty(videos)
	const hasPicture = !isEmpty(pictures)
	const hasLink = !isEmpty(links)

	return {
		[AttachmentType.PICTURE]: hasVideo || hasLink || pictures.length >= 9,
		[AttachmentType.VIDEO]: hasPicture || hasLink || videos.length >= 1,
		[AttachmentType.LINK]: hasVideo || hasPicture || links.length >= 1,
	}
}

export function filterFiles(files: File[]) {
	return files.reduce(
		(acc, item) => {
			if (([MIME_TYPES.png, MIME_TYPES.jpeg] as string[]).includes(item.type)) {
				acc.images.push({
					id: uniqueId(),
					file: item,
					status: AttachmentStatus.WAIT_TO_UPLOAD,
					type: AttachmentType.PICTURE,
				})
			} else if (([MIME_TYPES.mp4] as string[]).includes(item.type)) {
				acc.videos.push({
					id: uniqueId(),
					file: item,
					status: AttachmentStatus.WAIT_TO_UPLOAD,
					type: AttachmentType.VIDEO,
				})
			}
			return acc
		},
		{
			images: [],
			videos: [],
		} as {
			images: FileAttachment[]
			videos: FileAttachment[]
		}
	)
}

export function filterTransferItems(dataTransferItems: DataTransferItem[]) {
	return filterFiles(compact(dataTransferItems.map((item) => item.getAsFile())))
}

export function toAttachments(attachment: FormAttachment): Attachment[] {
	return attachment.ids.map((id) => attachment.map[id]).filter(Boolean) as Attachment[]
}
